import { CloseIcon } from "@/components/icons/close-icon";
import { SearchIcon } from "@/components/icons/search-icon";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

interface SearchBarProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  placeholder?: string;
  disabled?: boolean;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  onClear?: () => void;
  onSubmit?: (value: string) => void;
  className?: string;
  id?: string;
  enterKeyHint?: React.InputHTMLAttributes<HTMLInputElement>["enterKeyHint"];
  "aria-label"?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}

function SearchBarInner<T extends FieldValues>({
  control,
  name,
  placeholder = "Search",
  disabled = false,
  onBlur,
  onChange,
  onClear,
  onSubmit,
  className,
  id,
  enterKeyHint,
  "aria-label": ariaLabel,
  inputRef,
}: SearchBarProps<T>) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const hasValue = Boolean(field.value && String(field.value).trim());
        const state = isFocused ? "active" : hasValue ? "filled" : "empty";

        return (
          <div
            className={cn(
              "relative flex h-11 w-full items-center gap-2 rounded-full border bg-white px-3 py-2",
              "transition-colors duration-200",
              {
                "bg-gray-50": state === "active",
                "border-gray-200": state !== "active",
                "border-primary": state === "active",
              },
              className,
            )}
          >
            <SearchIcon className="h-5 w-5" />

            <input
              {...field}
              ref={inputRef}
              id={id}
              placeholder={placeholder}
              disabled={disabled}
              type="text"
              aria-label={ariaLabel}
              enterKeyHint={enterKeyHint}
              value={field.value ?? ""}
              onFocus={() => {
                setIsFocused(true);
              }}
              onBlur={() => {
                setIsFocused(false);
                field.onBlur();
                onBlur?.();
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  const searchQuery = String(field.value ?? "");

                  event.preventDefault();
                  onSubmit?.(searchQuery);

                  if (searchQuery !== "") {
                    event.currentTarget.blur();
                  }
                }
              }}
              onChange={(e) => {
                const value = e.target.value;
                field.onChange(value);
                onChange?.(value);
              }}
              className={cn(
                textVariants.bodySmall,
                "flex-1 !bg-transparent text-black outline-none",
                "placeholder:text-text-placeholder-default",
                "disabled:text-gray-400",
              )}
            />

            {hasValue && (
              <button
                type="button"
                aria-label="Clear search"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-black/70 transition-colors hover:bg-gray-400"
                onClick={() => {
                  field.onChange("");
                  onChange?.("");
                  onClear?.();
                }}
              >
                <CloseIcon className="h-4 w-4 text-white" />
              </button>
            )}
          </div>
        );
      }}
    />
  );
}

const SearchBar = memo(SearchBarInner) as typeof SearchBarInner;

export default SearchBar;
